import { postValidateOversellingInvoice } from "src/api/invoice";
import { notify } from "assets/scripts/utilities/api-clients";

export function useOverselling({ formData }) {
  const { sendInvoiceForApproval } = useInvoiceStore();

  const oversellingBanner = useTemplateRef("oversellingBanner");

  const isApprovalLoading = ref(false);
  const oversellingLineitems = ref([]);
  const invoiceOversellingPendingApproval = ref(false);

  const sendForApprovalSuppressed = ref(false);

  const isItemQtyChanged = () => {
    const items = unref(formData).lineitems;
    const uniqueItemRatePair = Object.values(
      items.reduce((acc, item) => {
        const key = `${item.item_id}_${item.po_rate}`;

        if (!acc[key]) {
          acc[key] = { ...item, qty: 0 };
        }

        acc[key].qty += item.qty;

        return acc;
      }, {}),
    );

    const oversellingItems = unref(oversellingLineitems);

    let isChanged = false;

    for (const oversellingItem of oversellingItems) {
      const previousQty = oversellingItem.qty;
      const currentQty = uniqueItemRatePair.find(
        (item) =>
          `${item.item_id}_${item.po_rate}` ==
          `${oversellingItem.item_id}_${oversellingItem.po_rate}`,
      )?.qty;

      if (previousQty != currentQty) {
        isChanged = true;
      }
    }

    return isChanged;
  };

  const displaySendForApprovalButton = computed(() => {
    return (
      unref(oversellingLineitems).length > 0 &&
      !isItemQtyChanged() &&
      !unref(sendForApprovalSuppressed)
    );
  });

  function addOversoldQtyToLineitems() {
    const oversellingMap = new Map();

    for (const item of oversellingLineitems.value) {
      oversellingMap.set(`${item.item_id}_${item.po_rate}`, item.overBy);
    }

    for (const lineitem of formData.value.lineitems) {
      if (oversellingMap.has(`${lineitem.item_id}_${lineitem.po_rate}`)) {
        lineitem.oversold_qty = oversellingMap.get(
          `${lineitem.item_id}_${lineitem.po_rate}`,
        );
      }
    }
  }

  async function validateLineitemsForOverselling() {
    notify({
      message: "Checking for overselling items",
      timeout: 1500,
    });

    const response = await postValidateOversellingInvoice(unref(formData));

    sendForApprovalSuppressed.value = false;

    oversellingLineitems.value = response.data?.data?.items;

    invoiceOversellingPendingApproval.value =
      response.data?.data?.is_pending_approval_conflict;

    if (
      unref(oversellingLineitems).length ||
      unref(invoiceOversellingPendingApproval)
    ) {
      nextTick(() => {
        if (oversellingBanner.value) {
          oversellingBanner.value.scrollIntoView({ behavior: "smooth" });
        }
      });

      return true;
    }

    return false;
  }

  async function sendApprovalRequest() {
    isApprovalLoading.value = true;

    addOversoldQtyToLineitems();

    await sendInvoiceForApproval(unref(formData));

    sendForApprovalSuppressed.value = false;

    isApprovalLoading.value = false;
  }

  const sendForApprovalButton = computed(() => {
    return {
      label: "Send for Approval",
      loading: unref(isApprovalLoading),
    };
  });

  const resetOversellingLineitems = () => {
    oversellingLineitems.value = [];

    invoiceOversellingPendingApproval.value = false;
  };

  return {
    oversellingLineitems,
    invoiceOversellingPendingApproval,
    sendForApprovalSuppressed,
    displaySendForApprovalButton,
    sendForApprovalButton,
    validateLineitemsForOverselling,
    sendApprovalRequest,
    resetOversellingLineitems,
  };
}
